import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import { useTextStyle } from '@bubble-ui/hook-use-text-style';
import { usePropsFromContext } from '@bubble-ui/provider-props-context';
import { Text } from '@bubble-ui/primitive-text';
import { getDisplayName } from '@bubble-ui/util-get-display-name';
import { isNative } from '@bubble-ui/util-platform';
import { getAccessibilityPresetAttrs } from './accessibilityPresets';
import { defaultComponentTokens } from './defaultComponentTokens';
import { TypographySkeleton } from './Skeleton';
var Typography = _ref => {
  var {
    accessibilityPreset = 'none',
    addRenderingBox = false,
    children,
    colorToken,
    colorTokenArgs,
    direction = 'ltr',
    fontFamily,
    fontSize,
    fontWeight,
    id,
    letterSpacing,
    lineHeight,
    numberOfLines = 0,
    shrink,
    testID,
    textAlignToken = 'typography/text-alignment',
    textAlignTokenArgs,
    textDecorationLine,
    textToken,
    textTokenArgs,
    UNSAFE_props
  } = _ref;
  var designContext = useDesignContext(defaultComponentTokens);
  var accPresetAttributes = getAccessibilityPresetAttrs(accessibilityPreset);
  var typographyPropsFromContext = usePropsFromContext(Typography.displayName);
  var textStyle = useTextStyle({
    addRenderingBox,
    colorToken: colorToken || typographyPropsFromContext.colorToken || 'typography/text-color',
    colorTokenArgs: colorTokenArgs || typographyPropsFromContext.colorTokenArgs,
    designTokens: defaultComponentTokens,
    direction,
    fontFamily: fontFamily || typographyPropsFromContext.fontFamily,
    fontSize: fontSize || typographyPropsFromContext.fontSize,
    fontWeight: fontWeight || typographyPropsFromContext.fontWeight,
    letterSpacing: letterSpacing || typographyPropsFromContext.letterSpacing,
    lineHeight: lineHeight || typographyPropsFromContext.lineHeight,
    numberOfLines,
    shrink,
    textAlignToken,
    textAlignTokenArgs,
    textDecorationLine: textDecorationLine || typographyPropsFromContext.textDecorationLine,
    textToken: textToken || typographyPropsFromContext.textToken || 'typography/text-style',
    textTokenArgs: textTokenArgs || typographyPropsFromContext.textTokenArgs
  });
  var nativeAttrs =
  // Ellipsis styling for web is handled by useTextStyle 👆
  isNative ? {
    numberOfLines,
    ellipsizeMode: 'tail'
  } : {};
  return /*#__PURE__*/React.createElement(Text, _extends({
    id: id,
    testID: testID
  }, UNSAFE_props, accPresetAttributes, nativeAttrs, {
    style: textStyle
  }), React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child) || typeof child !== 'object') {
      return child;
    }
    var childAsElement = child;
    var finalColorToken = childAsElement.props.colorToken || colorToken;
    var finalColorTokenArgs = childAsElement.props.colorTokenArgs || colorTokenArgs;
    var finalFontFamily = childAsElement.props.fontFamily || fontFamily;
    var finalFontSize = childAsElement.props.fontSize || fontSize;
    var finalFontWeight = childAsElement.props.fontWeight || fontWeight;
    var finalLetterSpacing = childAsElement.props.letterSpacing || letterSpacing;
    var finalLineHeight = childAsElement.props.lineHeight || lineHeight;
    var finalTextToken = childAsElement.props.textToken || textToken;
    var finalTextTokenArgs = childAsElement.props.textTokenArgs || textTokenArgs;
    if (getDisplayName(child) === 'Typography') {
      return /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
        context: designContext
      }, /*#__PURE__*/React.cloneElement(childAsElement, {
        colorToken: finalColorToken,
        colorTokenArgs: finalColorTokenArgs,
        fontFamily: finalFontFamily,
        fontSize: finalFontSize,
        fontWeight: finalFontWeight,
        letterSpacing: finalLetterSpacing,
        lineHeight: finalLineHeight,
        textToken: finalTextToken,
        textTokenArgs: finalTextTokenArgs
      }));
    } else if (getDisplayName(child) === 'Link') {
      return /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
        context: designContext
      }, /*#__PURE__*/React.cloneElement(childAsElement, {
        colorTokenArgs: finalColorTokenArgs,
        fontFamily: finalFontFamily,
        fontSize: finalFontSize,
        fontWeight: finalFontWeight,
        letterSpacing: finalLetterSpacing,
        lineHeight: finalLineHeight,
        textToken: finalTextToken,
        textTokenArgs: finalTextTokenArgs
      }));
    } else if (getDisplayName(child) === 'List') {
      return /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
        context: designContext
      }, /*#__PURE__*/React.cloneElement(childAsElement, {
        colorToken: finalColorToken,
        colorTokenArgs: finalColorTokenArgs,
        fontFamily: finalFontFamily,
        fontSize: finalFontSize,
        fontWeight: finalFontWeight,
        letterSpacing: finalLetterSpacing,
        lineHeight: finalLineHeight,
        textToken: finalTextToken,
        textTokenArgs: finalTextTokenArgs
      }));
    }
    return child;
  }));
};
Typography.displayName = 'Typography';
Typography.Skeleton = TypographySkeleton;
export { Typography };
//# sourceMappingURL=index.js.map